import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { api, inr, setSeo, formatApiErrorDetail, FREE_SHIPPING_THRESHOLD, SHIPPING_FEE } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const emptyForm = { full_name: "", mobile: "", email: "", address: "", city: "", state: "", pincode: "" };

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [processing, setProcessing] = useState(false);
  const discount = 0;

  const shipping = subtotal >= FREE_SHIPPING_THRESHOLD || subtotal === 0 ? 0 : SHIPPING_FEE;
  const total = Math.max(0, subtotal + shipping - discount);
  const orderMetas = useMemo(() => items.map((it) => [it.color, it.size].filter(Boolean).join(" · ")), [items]);

  useEffect(() => setSeo("Checkout | Adam's The Islamic Boutique", "Secure checkout."), []);
  useEffect(() => { if (items.length === 0 && !processing) navigate("/cart"); }, [items, processing, navigate]);
  useEffect(() => { if (user) setForm((f) => ({ ...f, full_name: f.full_name || user.name || "", email: f.email || user.email || "" })); }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [pinLoading, setPinLoading] = useState(false);
  const lookupPin = async (pin) => {
    setPinLoading(true);
    try {
      const { data } = await api.get(`/pincode/${pin}`);
      if (data.found) {
        setForm((f) => ({ ...f, city: data.city || f.city, state: data.state || f.state }));
        toast.success(`${data.city}, ${data.state}`, { description: "City & state filled from PIN code" });
      } else {
        toast.error("Couldn't detect that PIN code. Please enter city & state manually.");
      }
    } catch {
      /* network issue — user can still type city/state */
    } finally {
      setPinLoading(false);
    }
  };
  const onPinChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
    setForm((f) => ({ ...f, pincode: v }));
    if (v.length === 6) lookupPin(v);
  };

  const validate = () => {
    for (const [k, v] of Object.entries(form)) {
      if (!v.trim()) { toast.error(`Please enter your ${k.replace("_", " ")}`); return false; }
    }
    if (!/^\d{10}$/.test(form.mobile)) { toast.error("Enter a valid 10-digit mobile number"); return false; }
    if (!/^\d{6}$/.test(form.pincode)) { toast.error("Enter a valid 6-digit PIN code"); return false; }
    return true;
  };

  const placeOrder = async () => {
    if (!validate()) return;
    setProcessing(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, image: i.image, price: i.price, color: i.color, size: i.size, quantity: i.quantity })),
        subtotal, shipping, discount, coupon: null, total,
      };
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway. Please try again."); setProcessing(false); return; }
      const { data } = await api.post("/payments/razorpay/order", payload);
      try { localStorage.setItem("adams_pending_order", data.order_id); } catch { /* storage unavailable (private mode) — non-critical */ }
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Adam's The Islamic Boutique",
        description: "Order Payment",
        order_id: data.razorpay_order_id,
        prefill: { name: form.full_name, email: form.email, contact: form.mobile },
        notes: { address: form.address },
        theme: { color: "#C9A96A" },
        handler: async (res) => {
          try {
            await api.post("/payments/razorpay/verify", {
              order_id: data.order_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
          } catch (err) {
            toast.error("Payment verification failed. If money was deducted, please contact us.");
          }
          navigate(`/order/${data.order_id}`);
        },
        modal: { ondismiss: () => setProcessing(false) },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp.error?.description || "Payment failed. Please try again.");
        setProcessing(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
      setProcessing(false);
    }
  };

  const field = (label, key, type = "text", ph = "") => (
    <div>
      <label className="text-xs uppercase tracking-wider text-espresso/60 mb-1.5 block">{label}</label>
      <input type={type} value={form[key]} onChange={set(key)} placeholder={ph} className="w-full border border-espresso/20 rounded-xl px-4 py-3 bg-ivory outline-none focus:border-champagne text-sm" data-testid={`checkout-${key}`} />
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8 lg:py-12">
      <h1 className="font-serif text-4xl lg:text-5xl text-espresso mb-10">Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10">
        {/* Form */}
        <div>
          {!user && (
            <div className="flex flex-wrap items-center justify-between gap-2 bg-beige/60 rounded-2xl px-5 py-3.5 mb-6" data-testid="guest-checkout-banner">
              <p className="text-sm text-espresso/70">You're checking out as a <span className="font-medium text-espresso">guest</span> — no account needed.</p>
              <Link to="/login" className="text-sm text-champagne font-medium gold-underline">Sign in instead</Link>
            </div>
          )}
          <h2 className="font-serif text-2xl text-espresso mb-5">Shipping Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("Full Name", "full_name", "text", "Aisha Rahman")}
            {field("Mobile Number", "mobile", "tel", "10-digit number")}
            {field("Email", "email", "email", "you@example.com")}
            <div>
              <label className="text-xs uppercase tracking-wider text-espresso/60 mb-1.5 flex items-center gap-2">PIN Code {pinLoading && <span className="inline-flex items-center gap-1 text-champagne normal-case tracking-normal"><Loader2 className="w-3 h-3 animate-spin" /> detecting…</span>}</label>
              <input type="text" inputMode="numeric" value={form.pincode} onChange={onPinChange} placeholder="600040" className="w-full border border-espresso/20 rounded-xl px-4 py-3 bg-ivory outline-none focus:border-champagne text-sm" data-testid="checkout-pincode" />
            </div>
            <div className="sm:col-span-2">{field("Address", "address", "text", "House no, street, area")}</div>
            {field("City", "city", "text", "Chennai")}
            {field("State", "state", "text", "Tamil Nadu")}
          </div>

          <div className="mt-8 flex items-center gap-2 text-sm text-espresso/60 bg-beige/50 rounded-xl px-4 py-3">
            <ShieldCheck className="w-5 h-5 text-champagne" /> Secure payment powered by Razorpay
          </div>
        </div>

        {/* Summary */}
        <div className="bg-beige/50 rounded-3xl p-7 h-fit lg:sticky lg:top-32">
          <h2 className="font-serif text-2xl text-espresso mb-5">Order Summary</h2>
          <div className="space-y-4 max-h-64 overflow-y-auto mb-5">
            {items.map((it, idx) => (
              <div key={`${it.product_id}-${it.color || ""}-${it.size || ""}-${idx}`} className="flex gap-3">
                <img src={it.image} alt={it.name} className="w-14 h-16 object-cover rounded-lg" />
                <div className="flex-1 text-sm">
                  <p className="font-serif text-espresso leading-tight">{it.name}</p>
                  <p className="text-xs text-espresso/50">{orderMetas[idx]} × {it.quantity}</p>
                </div>
                <span className="text-sm font-medium">{inr(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>

          <div className="space-y-2.5 text-sm border-t border-champagne/25 pt-4">
            <div className="flex justify-between"><span className="text-espresso/60">Subtotal</span><span>{inr(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-espresso/60">Shipping</span><span>{shipping === 0 ? "Free" : inr(shipping)}</span></div>
          </div>
          <div className="border-t border-champagne/25 mt-4 pt-4 flex justify-between items-center">
            <span className="font-serif text-lg text-espresso">Total</span>
            <span className="font-serif text-2xl text-espresso" data-testid="checkout-total">{inr(total)}</span>
          </div>

          <button onClick={placeOrder} disabled={processing} className="w-full bg-champagne text-espresso py-4 rounded-full text-sm font-semibold mt-6 hover:bg-champagne-light transition-colors flex items-center justify-center gap-2 disabled:opacity-60" data-testid="checkout-razorpay-button">
            {processing ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : `Pay ${inr(total)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
